// What the caller is told about one workspace: every kind whose choices may be narrowed to a
// workspace and of which they may be told here, each channel's value here and where it came from.
// Writing here is "Follow this workspace" — a choice for this workspace alone, which wins over the
// caller's organization-wide one.

import { api } from '../client'
import type { components } from '../schema'
import type { UpdateNotificationChoices } from './preferences'

export type WorkspaceNotificationKindPreference = components['schemas']['WorkspaceNotificationKindPreference']
export type WorkspaceNotificationPreferences = components['schemas']['WorkspaceNotificationPreferencesView']

function preferencesPath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/notification-preferences`
}

/**
 * The caller's notifications in the workspace: `source` is `member_scoped` where they chose for this
 * workspace. 404 for a workspace of no concern to this organization, 403 for one the caller cannot
 * read; an empty list where nothing about this workspace may reach them.
 */
export function readWorkspaceNotificationPreferences(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<WorkspaceNotificationPreferences> {
  return api.get<WorkspaceNotificationPreferences>(preferencesPath(workspaceId), undefined, signal)
}

/**
 * Set (`true` follows, `false` excepts this workspace from an organization-wide "on") or clear
 * (`null`, back to the organization-wide value) the caller's choices for this workspace; the answer
 * is the view after the change. 400 for a kind that takes no workspace scope or a channel not
 * `selectable` yet, 422 for mail turned off on a mandatory kind.
 */
export function updateWorkspaceNotificationPreferences(
  workspaceId: string,
  body: UpdateNotificationChoices,
): Promise<WorkspaceNotificationPreferences> {
  return api.patch<WorkspaceNotificationPreferences>(preferencesPath(workspaceId), body)
}
